import type { TableCellProps } from '@chakra-ui/react'
import { Alert, Image, Stack, Table, Tbody, Td, Text, Tr } from '@chakra-ui/react'
import type { InsuranceParamsTravelDto, OfferPackage } from 'BrokerTsLib/src/dtos'
import { Insurer, PackageItemLimitType, SelectionType } from 'BrokerTsLib/src/enums'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import { format } from 'date-fns'
import { useContext } from 'react'

import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { useSelections } from '../../hooks'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { getLogo } from '../../utils/companiesLogos'
import { insurerOfferContext } from '../hoc/WithOffer'

interface RecapTableRowProps extends TableCellProps {
  col1: string | React.ReactNode
  col2?: string | React.ReactNode
  col3?: string | React.ReactNode
  isHighlighted?: boolean
}

export const RecapTableRow = ({ col1, col2, col3, isHighlighted, sx }: RecapTableRowProps) => {
  return (
    <Tr sx={{ m: 0, p: 0 }}>
      <Td sx={{ w: '15rem', p: 1, ...sx }}>
        {col1 && <Text sx={{ fontWeight: isHighlighted ? 'semibold' : 'medium' }}>{col1}</Text>}
      </Td>
      <Td sx={{ p: 1, ...sx }}>{col2 && col2}</Td>
      {col3 && (
        <Td sx={{ p: 1, fontWeight: isHighlighted ? 'semibold' : 'normal', textAlign: 'end', ...sx }}>
          {col3}
        </Td>
      )}
    </Tr>
  )
}

export const Recap = () => {
  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const pfEnum = dataPf ?? []

  const { insuranceParams: ip, } = useInsuranceParamsContext()
  const insuranceParams = ip as InsuranceParamsTravelDto
  const { isLoading, offer: o } = useContext(insurerOfferContext)
  const offer = o as OfferPackage
  const cat = insuranceParams?.generalParams.contractAcceptationType
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const isCanceled = !!insuranceParams?.generalParams.contractCanceled

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace pojištění</FormSectionTitle>

        {offer &&
          <FormSectionDescription>
            <Image src={getLogo(offer.insurerId!)} alt={offer.insurerNameFull} width="64px" maxH="48px" />
          </FormSectionDescription>
        }
      </FormSectionHeader>

      {!offer && isLoading && <Loading large={true} />}
      {offer &&
        <FormSectionBody>
          <Table variant="unstyled">
            <Tbody>
              <RecapTableRow col1="Typ pojištění" col2={`Cestovní pojištění ${offer?.title}`} />

              {offer.parts
                .filter(x => x.valid && x.included && !(x.packageItem?.isAddition ?? false))
                .map((part, index) => {
                  const limitText = part.limit === 999999999
                    ? 'Neomezeně'
                    : part.limit
                      ? formatPrice({ price: part.limit })
                      : part.packageItem?.limitType === PackageItemLimitType.Limit
                        ? '0 Kč'
                        : 'Ano'

                  return <RecapTableRow key={part.riskId}
                    col1={index === 0 ? 'Rozsah pojištění' : ''}
                    col2={part.name}
                    col3={limitText}
                  />})}

              <RecapTableRow
                isHighlighted
                col1="Celkem"
                col2={<Text sx={{ fontWeight: 'semibold' }}>{formatPrice({ price: offer.price })}</Text>}
              />
            </Tbody>
          </Table>

          <Table variant="unstyled">
            <Tbody>

              <RecapTableRow col1="Splátky pojistného" col2={pfEnum.find(x => x.id === offer.paymentFrequencyId)?.description} />
              <RecapTableRow col1="Splátka" col2={formatPrice({ price: offer.priceFrequency })} />
              <RecapTableRow col1="Počátek pojištění" col2={format(new Date(insuranceParams!.generalParams.insuranceStartDate!), 'd. M. yyyy')} />
              {(insuranceParams as InsuranceParamsTravelDto)?.travel.insuranceEndDate &&
                <RecapTableRow col1="Konec pojištění" col2={format(new Date(insuranceParams!.travel.insuranceEndDate!), 'd. M. yyyy')} />
              }

              {isContracted &&
                <>
                  {/* <RecapTableRow col1="Způsob sjednání" col2={fsEnum.find(x => x.value === insuranceParams?.generalParams.contractAcceptationType?.toString())?.description} /> */}
                  <RecapTableRow col1="Datum sjednání" col2={format(new Date(insuranceParams!.generalParams.contractCreatedDate!), 'd. M. yyyy HH:mm')} />
                </>
              }

              {isContracted && cat === 2 &&
                <RecapTableRow col1="Splatnost pojistného"
                  col2={insuranceParams?.generalParams.paymentDateSpecial ?
                    insuranceParams.generalParams.paymentDateSpecial :
                    format(new Date(insuranceParams!.generalParams.paymentDate!), 'd. M. yyyy')} />
              }
              {isCanceled &&
                <RecapTableRow col1="Smlouva byla stornována" col2={`${format(new Date(insuranceParams.generalParams.contractCanceledDate!), 'd. M. yyyy HH:mm')}, ${insuranceParams.generalParams.contractCanceledBy}`} />
              }
            </Tbody>
          </Table>

          {isContracted && (cat === 2 || offer.infos) &&
            <Alert colorScheme="orange">
              <Stack direction='column'>
                {cat === 2 && <Text>Pokud nebude první pojistné uhrazeno v termínu, PS nevznikne!</Text>}

                {cat === 2 && offer.insurerId == Insurer.Allianz && <>
                  <Text>
                    Upozornění pro poradce: {offer.insurerNameFull} neumožňuje stažení dokumentu s názvem Návrh pojistné smlouvy.
                    Tento dokument bude na klienta i Vás jako poradce odeslán přímo od pojišťovny v samostatném e-mailu.
                  </Text>
                  <Text>
                    Doložte kopii e-mailu z pojišťovny do Vašeho informačního systému!
                  </Text>
                </>}

                {offer.infos && offer.infos.filter(x => x.message).map(x => (
                  <Text dangerouslySetInnerHTML={{ __html: x.message }}></Text>
                ))}
              </Stack>
            </Alert>
          }
        </FormSectionBody>
      }
    </FormSection>
  )
}
